import Color from "color";
import React, { useCallback } from "react";
import styled from "styled-components";

import { Errors } from "--root/Errors";
import { producerSitemap } from "--root/sitemaps/sitemaps";
import { Strings } from "--root/strings/en";
import { getReadableRelativeTime } from "--root/utils/datetime";

import { Body1, Body2, Heading5 } from "--components/typography";

import { SwaggerMutateClient } from "--client/common/apis/rest";

import { Button } from "--floponents-web/atoms/Button";
import { ListEntry, ListEntryAction, ListEntryText } from "--floponents-web/atoms/ListEntry";
import { SurfaceHeader } from "--floponents-web/atoms/Surface";
import { DialogCloseButton } from "--floponents-web/molecules/DialogCloseButton/DialogCloseButton";
import { useToastNotifications } from "--floponents-web/molecules/ToastNotification/ToastProvider";

import { useStableHistory } from "../../../common/hooks/useStableHistory";
import { useCemQueue } from "./useCemQueue";

const StyledTextLink = styled.a`
    color: ${props => props.theme.palette.text.link};
    text-decoration: none;

    :hover,
    :active {
        text-decoration: underline;
    }

    :visited {
        color: ${props => Color(props.theme.palette.text.link).desaturate(0.5).toString()};
    }
`;
interface TextLinkProps extends React.HtmlHTMLAttributes<HTMLAnchorElement> {
    path: string;
}
const TextLink = (props: TextLinkProps) => {
    return (
        <StyledTextLink
            onClick={props.onClick}
            href={`${window.location.origin}/#${props.path}`}
        >
            {props.children}
        </StyledTextLink>
    );
}

const UnadoptedStreams = styled.ul`
    display: flex;
    flex-direction: column;
    list-style: none;
`;

export interface CemQueueDialogProps {
    spaceId: string;
    onClose?: () => void;
}
export const CemQueueDialog = (props: CemQueueDialogProps) => {
    const cemQueue = useCemQueue(props.spaceId);

    return <>
        <SurfaceHeader>
            <Heading5>{Strings.inline("CEM Queue")}</Heading5>
            <DialogCloseButton onClick={props.onClose} />
        </SurfaceHeader>
        <UnadoptedStreams>
            {cemQueue && cemQueue.length > 0
                ? cemQueue?.map(stream => (
                    <CemQueueItem
                        name={stream.name}
                        streamId={stream.id}
                        lastActive={stream.lastActive}
                        key={stream.id}
                        onClose={props.onClose}
                    />
                ))
                : <Body2>{Strings.inline("No unadopted streams...")}</Body2>}
        </UnadoptedStreams>
    </>;
};

interface CemQueueItemProps {
    name: string;
    streamId: string;
    lastActive: number;
    onClose?: () => void;
}
const CemQueueItem = (props: CemQueueItemProps) => {
    const { onClose } = props;

    const history = useStableHistory();
    const toast = useToastNotifications();

    const streamPath = producerSitemap.thread.$$createUrl({
        thread_id: props.streamId,
        scrollToIndex: -1
    });

    const handleAdoptStream = useCallback(() => {
        SwaggerMutateClient.AdoptStream({ id: props.streamId })
            .catch(err => {
                if (err instanceof Errors.AlreadyAdoptedError) {
                    toast.addToast(
                        <Body1>{Strings.alreadyAdopted}</Body1>
                    );
                }
            });

        history.push(streamPath);
        onClose?.();
    }, [history, onClose, props.streamId, streamPath, toast]);

    return (
        <ListEntry key={props.streamId}>
            <ListEntryText
                primary={
                    <TextLink path={streamPath} onClick={props.onClose}>
                        {props.name}
                    </TextLink>
                }
                secondary={
                    <Strings.Inline>
                        Last active {getReadableRelativeTime(props.lastActive)}
                    </Strings.Inline>
                }
            />

            <ListEntryAction>
                <Button onClick={handleAdoptStream}>
                    {Strings.inline("Adopt")}
                </Button>
            </ListEntryAction>
        </ListEntry>
    );
};
